import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useRef } from 'react';
import { Button, Card, Column, Row, TextButton, Typography, CloseButton } from 'seagull';
import { LogoPicker } from '~/components/LogoPicker/LogoPicker';
import { useScrollBorder } from '~/hooks/useScrollBorder';
import { useLocaleKeys } from '~/i18n';
import { makeDefaultBranding } from '~/store/store.utils';
import { useBrandingActions } from '../BrandingActionsContext';
import { PreviewState } from '../ClientOnboarding.utils';
import { useCobBranding } from '../CobBrandingContext';
import { useCobConfig } from '../CobConfigContext';
import styles from './BrandingControllers.module.scss';
import { ColorsLabel, SectionTitle } from './BrandingControllers.shared';
import { ColorSelectorWithPopup } from './ColorSelectorWithPopup';
import { PrimaryBackgroundColorPopup } from './PrimaryBackgroundColorPopup';
const defaults = makeDefaultBranding();
function syncDerivedBrandingProperties(_prev, next) {
    return _object_spread_props(_object_spread({}, next), {
        progressBarComplete: next.buttonPrimary,
        buttonSecondary: defaults.buttonSecondary,
        buttonDisabled: defaults.buttonDisabled
    });
}
export function BrandingPanel() {
    const { branding, logoImage, errors, isDirty, isValid, setBranding: setBrandingContext, setLogo, revertToDefault, saveLogoAndBranding } = useCobBranding();
    const { t: texts } = useLocaleKeys('clientFlow.brandingPanel');
    const { t: logoPickerTexts } = useLocaleKeys('components.logoPicker');
    const setBranding = useCallback((update)=>{
        setBrandingContext((prev)=>{
            const next = typeof update === 'function' ? update(prev) : update;
            return syncDerivedBrandingProperties(prev, next);
        });
    }, [
        setBrandingContext
    ]);
    const { originalPreviewState } = useCobConfig();
    const { onToggleBrandSetup } = useBrandingActions();
    function handleCloseClick() {
        onToggleBrandSetup();
    }
    const contentRef = useRef(null);
    const { headerClassName, footerClassName } = useScrollBorder({
        contentRef,
        headerBaseClassName: styles.headerSection,
        footerBaseClassName: styles.saveSection
    }, []);
    return /*#__PURE__*/ _jsxs(Card, {
        bi: {
            name: 'branding_panel_card'
        },
        className: styles.brandingCard,
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                alignItems: "center",
                justifyContent: "space-between",
                className: headerClassName,
                children: [
                    /*#__PURE__*/ _jsx(SectionTitle, {
                        variant: "paragraph1",
                        children: texts.customizeBrand()
                    }),
                    originalPreviewState !== PreviewState.SetupBrand && /*#__PURE__*/ _jsx(CloseButton, {
                        bi: {
                            name: 'close_branding_setup'
                        },
                        skin: "standard",
                        onClick: handleCloseClick,
                        testId: "close-branding-setup-btn"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                ref: contentRef,
                className: styles.scrollableContent,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.logoSection,
                        children: /*#__PURE__*/ _jsx(Column, {
                            gap: 12,
                            children: /*#__PURE__*/ _jsx(LogoPicker, {
                                testId: "upload-logo",
                                image: logoImage,
                                onChange: setLogo,
                                showEditButton: true,
                                editButtonLabel: logoPickerTexts.changeFile()
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.colorsSection,
                        children: /*#__PURE__*/ _jsxs(Column, {
                            gap: 24,
                            children: [
                                /*#__PURE__*/ _jsxs(Row, {
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    children: [
                                        /*#__PURE__*/ _jsx(ColorsLabel, {
                                            children: texts.brandColor()
                                        }),
                                        /*#__PURE__*/ _jsx(Row, {
                                            gap: 6,
                                            children: /*#__PURE__*/ _jsx(ColorSelectorWithPopup, {
                                                branding: branding,
                                                hasError: errors.has('buttonPrimary'),
                                                colorKey: "buttonPrimary",
                                                onChange: setBranding
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Row, {
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    children: [
                                        /*#__PURE__*/ _jsx(ColorsLabel, {
                                            children: texts.primaryBackground()
                                        }),
                                        /*#__PURE__*/ _jsx(Row, {
                                            gap: 6,
                                            children: /*#__PURE__*/ _jsx(PrimaryBackgroundColorPopup, {
                                                branding: branding,
                                                hasError: errors.has('backgroundGradientStart') || errors.has('backgroundGradientEnd'),
                                                onChange: setBranding
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Row, {
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    children: [
                                        /*#__PURE__*/ _jsx(ColorsLabel, {
                                            children: texts.secondaryBackground()
                                        }),
                                        /*#__PURE__*/ _jsx(Row, {
                                            gap: 6,
                                            children: /*#__PURE__*/ _jsx(ColorSelectorWithPopup, {
                                                branding: branding,
                                                hasError: errors.has('backgroundStandard'),
                                                colorKey: "backgroundStandard",
                                                onChange: setBranding
                                            })
                                        })
                                    ]
                                })
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Column, {
                gap: 12,
                alignItems: "center",
                className: footerClassName,
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'save_branding_colors'
                        },
                        skin: "primaryOutline",
                        disabled: !isDirty || !isValid,
                        testId: "save-colors",
                        tooltip: {
                            placement: 'top',
                            title: !isDirty ? texts.nothingToSave() : !isValid ? texts.oneOfTheFieldsIsInvalid() : ''
                        },
                        onClick: saveLogoAndBranding,
                        className: styles.saveBtn,
                        children: texts.saveChanges()
                    }),
                    /*#__PURE__*/ _jsx(TextButton, {
                        bi: {
                            name: 'revert_default_colors'
                        },
                        testId: "undo-color-changes",
                        variant: "paragraph2",
                        skin: "primary",
                        underline: "none",
                        weight: "normal",
                        onClick: revertToDefault,
                        children: texts.revertToDefaultColors()
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph3",
                        skin: "secondary",
                        className: styles.disclaimer,
                        children: texts.changesSavedInfo()
                    })
                ]
            })
        ]
    });
}
